/**
 * Register a document, then read it.
 *
 * TWO SERVER ACTS, NOT ONE, AND THE SCREEN KEEPS THEM APART.
 *
 *   1. `POST .../documents`                  records the file's IDENTITY.
 *                                            No bytes are sent or stored.
 *   2. `POST .../call-sheet-extractions`     sends the exact bytes to be read.
 *
 * They are separate because they mean different things. Registration says
 * "this file exists, here is where it lives, and this is how you can tell it
 * apart from a changed copy". Reading it is a later act that can happen many
 * times, and each reading is kept. Merging them here to save the user a click
 * would put the frontend's convenience ahead of a distinction the whole
 * evidence chain rests on.
 *
 * WHAT THE BROWSER CAN AND CANNOT SUPPLY. Five of the six required fields
 * come off the `File` object. The sixth, `sha256`, is computed here with
 * `crypto.subtle` — which does NOT weaken the gate: the server hashes what it
 * receives and compares it with what was registered, so a wrong digest here
 * produces a refusal there rather than an accepted lie.
 *
 * `storage_uri` IS THE ONE THE BROWSER CANNOT KNOW. It says where the
 * canonical file lives — a Drive link, a share path — and neither the file
 * picker nor the server can answer it. So it is asked for, in those words.
 *
 * NOTHING HERE CONFIRMS ANYTHING. Reading a document produces suggestions; a
 * person puts one onto a unit-day they choose, on the review screen.
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { sha256Hex } from '../../api/client';
import { DOCUMENT_KINDS, modeller, type DocumentKind } from '../api';
import { PageHeader } from '../shell/Shell';
import { Note, Tag } from '../shell/provenance';
import './newproduction.css';

function bytes(n: number): string {
  if (n >= 1_048_576) return `${(n / 1_048_576).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

const KIND_LABEL: Record<DocumentKind, string> = {
  call_sheet: 'Call sheet',
  schedule: 'Schedule',
  script: 'Script',
  equipment_list: 'Equipment list',
  other: 'Other',
};

/**
 * The one refusal a person meets by accident, translated.
 *
 * `source_document_unique_content` is UNIQUE (production_id, sha256), and the
 * route does not wrap it — so registering the same call sheet twice returns a
 * 409 carrying a raw Postgres constraint violation. That is a correct refusal
 * with an unusable sentence, and it reaches a form now rather than a script.
 * Translated HERE rather than server-side because this slice does not change
 * readapi; the server's own wording is the right place for it eventually.
 */
function registrationMessage(detail: string): string {
  if (detail.includes('source_document_unique_content')) {
    return 'This exact file is already registered on this production. '
      + 'A file is recognised by its fingerprint, so the same bytes cannot be '
      + 'registered twice — open it from the documents list instead.';
  }
  return detail;
}

/** Some files arrive with an empty `type` — the browser guesses from the
 *  extension and a `.xls` from a share drive often defeats it. The server
 *  records this and gates nothing on it (the adapters fingerprint the bytes),
 *  but it must be a non-empty string. */
function mediaType(file: File): string {
  return file.type || 'application/octet-stream';
}

/**
 * WHAT IS HAPPENING, not what is known. The fingerprint is deliberately NOT
 * carried in here: an earlier version made it `{ t: 'ready'; sha }`, so the
 * moment the step advanced to 'registering' the panel rendered an em dash
 * where the fingerprint had been — the value had not gone anywhere, the state
 * machine had simply stopped being able to say it. A fact and a phase are
 * different things and are held separately.
 */
type Step =
  | { t: 'choosing' }
  | { t: 'hashing' }
  | { t: 'ready' }
  | { t: 'registering' }
  | { t: 'reading'; documentId: number }
  | { t: 'failed'; where: string; message: string };

export function NewDocument() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [sha, setSha] = useState<string | null>(null);
  const [kind, setKind] = useState<DocumentKind>('call_sheet');
  const [storageUri, setStorageUri] = useState('');
  const [who, setWho] = useState('');
  const [step, setStep] = useState<Step>({ t: 'choosing' });

  const busy = step.t === 'hashing' || step.t === 'registering' || step.t === 'reading';

  async function pick(chosen: File | null) {
    setFile(chosen);
    setSha(null);
    if (!chosen) { setStep({ t: 'choosing' }); return; }
    setStep({ t: 'hashing' });
    try {
      setSha(await sha256Hex(chosen));
      setStep({ t: 'ready' });
    } catch (err) {
      setStep({
        t: 'failed', where: 'fingerprinting this file',
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!file || !sha) return;

    let documentId: number;
    setStep({ t: 'registering' });
    try {
      const doc = await modeller.registerDocument(slug, {
        kind,
        filename: file.name,
        media_type: mediaType(file),
        byte_size: file.size,
        sha256: sha,
        storage_uri: storageUri.trim(),
        uploaded_by: who.trim() || null,
      });
      documentId = doc.id;
    } catch (err) {
      setStep({
        t: 'failed', where: 'registering the document',
        message: registrationMessage(
          err instanceof Error ? err.message : String(err)),
      });
      return;
    }

    /* A call sheet goes straight on to be read. Anything else stops here:
       only the call-sheet adapters exist, and offering to read a script would
       promise a parse that has no implementation. */
    if (kind !== 'call_sheet') {
      navigate(`/modeller/productions/${slug}/documents`);
      return;
    }

    setStep({ t: 'reading', documentId });
    try {
      const result = await modeller.extractCallSheet(
        slug, documentId, file, who.trim() || 'unnamed');
      navigate(
        `/modeller/productions/${slug}/extractions/${result.extraction.id}`);
    } catch (err) {
      setStep({
        t: 'failed',
        /* Named precisely. The document IS registered at this point, and
           telling someone registration failed would send them to do it
           again and hit a duplicate. */
        where: 'reading the call sheet (the document is registered)',
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }

  const ready = file !== null && sha !== null && storageUri.trim() !== '';

  return (
    <>
      <PageHeader title="Register a document" count={slug} />
      <form className="pem-form" onSubmit={submit}>
        <label className="pem-field">
          <span className="pem-label">File</span>
          <input type="file" onChange={(e) => void pick(e.target.files?.[0] ?? null)} />
          <span className="pem-provenance">
            The exact source file. A re-saved or re-exported copy is a
            different file and is refused when it is read.
          </span>
        </label>

        {file && (
          <div className="pem-form__identity">
            <div className="pem-form__identity-row">
              <span className="pem-label">Name</span><b>{file.name}</b>
            </div>
            <div className="pem-form__identity-row">
              <span className="pem-label">Size</span><span>{bytes(file.size)}</span>
            </div>
            <div className="pem-form__identity-row">
              <span className="pem-label">Fingerprint</span>
              {step.t === 'hashing'
                ? <span className="pem-provenance">calculating</span>
                : sha
                  ? <code title={sha}>{sha.slice(0, 24)}…</code>
                  : <span className="pem-provenance">—</span>}
            </div>
            <Note>
              The fingerprint is calculated from the file itself, here in the
              browser. It is what tells this file apart from a changed copy
              later.
            </Note>
          </div>
        )}

        <div className="pem-form__pair">
          <label className="pem-field">
            <span className="pem-label">Kind</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as DocumentKind)}>
              {DOCUMENT_KINDS.map((k) => (
                <option key={k} value={k}>{KIND_LABEL[k]}</option>
              ))}
            </select>
            <span className="pem-provenance">
              {kind === 'call_sheet'
                ? 'Call sheets are read automatically once registered.'
                : 'Recorded only. This kind is not read automatically.'}
            </span>
          </label>
          <label className="pem-field">
            <span className="pem-label">Registered by</span>
            <input value={who} onChange={(e) => setWho(e.target.value)}
                   placeholder="your name" />
          </label>
        </div>

        <label className="pem-field">
          <span className="pem-label">Where the file lives</span>
          <input value={storageUri} required
                 onChange={(e) => setStorageUri(e.target.value)}
                 placeholder="a link or path to the original" />
          <span className="pem-provenance">
            This system records the file, it does not keep it. This is the only
            thing that says where the original can be found again.
          </span>
        </label>

        <div className="pem-form__actions">
          <button type="submit" className="pem-btn" disabled={busy || !ready}>
            {step.t === 'registering' ? 'Registering'
              : step.t === 'reading' ? 'Reading the call sheet'
                : kind === 'call_sheet' ? 'Register and read' : 'Register'}
          </button>
          <button type="button" className="pem-btn pem-btn--quiet"
                  onClick={() => navigate(`/modeller/productions/${slug}/documents`)}>
            Cancel
          </button>
        </div>

        {step.t === 'failed' && (
          <div className="pem-form__error">
            <Tag tone="caution">Stopped while {step.where}</Tag>
            <p>{step.message}</p>
          </div>
        )}

        <Note>
          Registering records the file. Reading it produces suggestions.
          Putting anything it says onto a shooting day is a third act that only
          a person performs.
        </Note>
      </form>
    </>
  );
}
